import BaseModel from 'volta/models/base-model';
import { resourceCommand } from 'volta/utils/api';

import { attr } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';

import type { TEntityType } from './types/entity-types';
import type { TTheme } from 'volta/modifiers/theme-mode';
import type {
  IDashboardWidget,
  IMenuPreferences,
  IPagesPreferences,
  IUpdateDashboardCmd,
  IUpdateInterfaceCmd,
  IUpdatePreferencesCmd
} from './types/user';
import type User from './user';

export const UserPreferencesResourceName = {
  singular: 'userPreference',
  plural: 'userPreferences'
};

export const UserPreferencesCommands = {
  UpdateInterface: 'UpdateInterface',
  UpdatePreferences: 'UpdatePreferences',
  UpdateDashboard: 'UpdateDashboard'
};

export default class UserPreference extends BaseModel {
  /**
   * Commands
   */
  updateInterface = resourceCommand<IUpdateInterfaceCmd, UserPreference>(
    UserPreferencesCommands.UpdateInterface
  );
  updatePreferences = resourceCommand<IUpdatePreferencesCmd, UserPreference>(
    UserPreferencesCommands.UpdatePreferences
  );
  updateDashboard = resourceCommand<IUpdateDashboardCmd, UserPreference>(
    UserPreferencesCommands.UpdateDashboard
  );

  @attr('string')
  userId!: string;

  @attr('string', { defaultValue: 'en-gb' })
  locale?: string;

  @attr('string', { defaultValue: 'Europe/Paris' })
  timeZone?: string;

  @attr('string')
  avatar?: string;

  @attr('string')
  theme!: TTheme;

  @attr('array')
  dashboard!: IDashboardWidget[];

  @attr()
  menu!: IMenuPreferences;

  @attr({ defaultValue: () => ({}) })
  pages?: Record<string, IPagesPreferences>;

  @attr({ defaultValue: () => ({}) })
  collectionViewsOrder?: Partial<Record<TEntityType, string[]>>;

  /**
   * Not a real attributes, but used to store the home link
   * We should create a feature in the future
   */
  @attr('string')
  homeRoute?: string;

  /**
   * Will be populated by the session-user service on app init
   */
  @tracked
  declare user: User;

  get profilePrefs(): IUpdatePreferencesCmd {
    return {
      locale: this.locale,
      timeZone: this.timeZone,
      avatar: this.avatar,
      theme: this.theme
    };
  }

  getPagePreferences<T = unknown>(key: string) {
    return this.pages?.[key] as IPagesPreferences<T> | undefined;
  }

  [Type] = 'user-preference' as const;
}
